import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { AiVisPlusIcon } from './AiVisNavIcons';
import { getAiVisCssVars } from './aivisTheme';

function CampaignLogo({ url, label, className }) {
  const [ok, setOk] = useState(true);
  if (!url || !ok) return null;
  return (
    <img
      src={url}
      alt=""
      className={className}
      loading="lazy"
      decoding="async"
      onError={() => setOk(false)}
    />
  );
}

export default function CampaignDropdown({
  campaigns,
  selectedId,
  onSelectId,
  canCreateCampaign,
  collapsed,
}) {
  const [open, setOpen] = useState(false);
  const [panelMounted, setPanelMounted] = useState(false);
  const [panelClosing, setPanelClosing] = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [panelStyle, setPanelStyle] = useState({});

  const positionPanel = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const minW = 260;
    setPanelStyle({
      position: 'fixed',
      top: rect.bottom + 6,
      left: rect.left,
      width: Math.max(minW, rect.width),
      zIndex: 10000,
    });
  }, []);

  useLayoutEffect(() => {
    if (!panelMounted) return;
    positionPanel();
  }, [panelMounted, collapsed, positionPanel, selectedId]);

  useEffect(() => {
    if (!panelMounted) return;
    const upd = () => positionPanel();
    window.addEventListener('scroll', upd, true);
    window.addEventListener('resize', upd);
    return () => {
      window.removeEventListener('scroll', upd, true);
      window.removeEventListener('resize', upd);
    };
  }, [panelMounted, positionPanel]);

  const closePanel = useCallback(() => {
    setPanelClosing(true);
    setOpen(false);
    // panelMounted is cleared when the close animation ends (onAnimationEnd)
  }, []);

  const openPanel = useCallback(() => {
    setPanelClosing(false);
    setPanelMounted(true);
    setOpen(true);
  }, []);

  function handlePanelAnimationEnd() {
    if (panelClosing) {
      setPanelMounted(false);
      setPanelClosing(false);
      setSearch('');
    }
  }

  useEffect(() => {
    function onDoc(e) {
      const t = e.target;
      if (wrapRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      closePanel();
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [closePanel]);

  const selected = useMemo(
    () => campaigns.find((c) => String(c.id) === String(selectedId)),
    [campaigns, selectedId]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return campaigns;
    return campaigns.filter((c) => {
      const name = (c.name || '').toLowerCase();
      const client = (c.client_name || '').toLowerCase();
      return name.includes(q) || client.includes(q);
    });
  }, [campaigns, search]);

  const pick = useCallback(
    (id) => {
      onSelectId(id);
      closePanel();
    },
    [onSelectId, closePanel]
  );

  const label = selected ? selected.name : 'Select client';

  const panel = panelMounted ? (
    <div
      ref={panelRef}
      className={`aivis-campaign-dd-panel${panelClosing ? ' aivis-campaign-dd-panel--closing' : ''}`}
      style={{ ...getAiVisCssVars(), ...panelStyle }}
      role="presentation"
      onAnimationEnd={handlePanelAnimationEnd}
    >
      <div className="aivis-campaign-dd-search">
        <input
          type="search"
          className="aivis-campaign-dd-search-input"
          placeholder="Search clients"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          autoComplete="off"
        />
      </div>
      <ul className="aivis-campaign-dd-list" role="listbox" aria-label="Clients">
        {!filtered.length ? (
          <li className="aivis-campaign-dd-empty">No clients match.</li>
        ) : (
          filtered.map((c) => {
            const isSel = String(c.id) === String(selectedId);
            return (
              <li key={c.id} role="option" aria-selected={isSel}>
                <button
                  type="button"
                  className={`aivis-campaign-dd-item ${isSel ? 'aivis-campaign-dd-item-active' : ''}`}
                  onClick={() => pick(c.id)}
                >
                  <span className="aivis-campaign-dd-item-row">
                    <CampaignLogo
                      url={c.client_logo_url}
                      label={c.name}
                      className="aivis-campaign-dd-item-logo"
                    />
                    <span className="aivis-campaign-dd-item-text">
                      <span className="aivis-campaign-dd-item-name">{c.name}</span>
                      <span className="aivis-campaign-dd-item-sub">{c.client_name}</span>
                      {c.enabled === false ? (
                        <span className="aivis-status-capsule aivis-status-capsule--paused aivis-campaign-dd-item-capsule">
                          Paused
                        </span>
                      ) : null}
                    </span>
                  </span>
                </button>
              </li>
            );
          })
        )}
      </ul>
      {canCreateCampaign ? (
        <Link
          to="/ai-vis/campaigns/new"
          className="aivis-campaign-dd-add"
          onClick={closePanel}
        >
          <span className="aivis-campaign-dd-add-icon" aria-hidden>
            <AiVisPlusIcon />
          </span>
          <span>Add new Client</span>
        </Link>
      ) : null}
    </div>
  ) : null;

  return (
    <div className="aivis-campaign-dd" ref={wrapRef}>
      <button
        type="button"
        ref={triggerRef}
        className={`aivis-campaign-dd-trigger ${collapsed ? 'aivis-campaign-dd-trigger-collapsed' : ''}`}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={selected ? `${selected.name}${selected.client_name ? ` — ${selected.client_name}` : ''}` : 'Select client'}
        onClick={(e) => {
          e.stopPropagation();
          if (open) closePanel();
          else openPanel();
        }}
      >
        {!collapsed && selected?.client_logo_url ? (
          <CampaignLogo
            url={selected.client_logo_url}
            label={selected.name}
            className="aivis-campaign-dd-trigger-logo-inline"
          />
        ) : null}
        <span className="aivis-campaign-dd-trigger-text">{label}</span>
        {collapsed && selected?.client_logo_url ? (
          <CampaignLogo
            url={selected.client_logo_url}
            label={selected.name}
            className="aivis-campaign-dd-trigger-logo"
          />
        ) : null}
        {collapsed && selected && !selected.client_logo_url ? (
          <span className="aivis-campaign-dd-trigger-initial" aria-hidden>
            {(selected.name || '?').trim().charAt(0).toUpperCase() || '?'}
          </span>
        ) : null}
        {collapsed && !selected ? (
          /* Placeholder icon when no client is selected */
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <circle cx="12" cy="8" r="4" />
            <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
          </svg>
        ) : null}
        {!collapsed ? (
          <span className="aivis-campaign-dd-chevron" aria-hidden>
            <svg
              className="aivis-campaign-dd-chevron-svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6 9l6 6 6-6"
                stroke="currentColor"
                strokeWidth="2.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        ) : null}
      </button>
      {panel ? createPortal(panel, document.body) : null}
    </div>
  );
}
